import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Chip,
  Slider,
  Menu,
  MenuItem,
  Divider,
  TextField,
  InputAdornment,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  Collapse,
  Alert,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Timeline as TimelineIcon,
  Settings as SettingsIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  FilterList as FilterListIcon,
  Event as EventIcon,
  Flag as FlagIcon,
  InfoOutlined as InfoOutlinedIcon,
  Search as SearchIcon,
  ViewList as ViewListIcon,
  ViewTimeline as ViewTimelineIcon,
  DragIndicator as DragIndicatorIcon,
  MoreVert as MoreVertIcon,
  Help as HelpIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import TimelineEventDialog from './TimelineEventDialog';
import TimelineEraManager from './TimelineEraManager';
import TimelineEventList from './TimelineEventList';
import { useTheme } from '@mui/material/styles';

const DEFAULT_SETTINGS = {
  startYear: 0,
  endYear: 1000,
  yearLabel: 'CE',
  displayYears: true
};

const TimelineEditor = ({ 
  timeline = { 
    events: [], 
    eras: [],
    settings: DEFAULT_SETTINGS
  }, 
  onTimelineChange = () => {},
  onAddEvent = (event) => {
    const newEvents = [...timeline.events, event];
    onTimelineChange({ ...timeline, events: newEvents });
  },
  onEditEvent = (event) => {
    const updatedEvents = timeline.events.map(e => 
      e.id === event.id ? event : e
    );
    onTimelineChange({ ...timeline, events: updatedEvents });
  },
  onDeleteEvent = (eventId) => {
    const updatedEvents = timeline.events.filter(e => e.id !== eventId);
    onTimelineChange({ ...timeline, events: updatedEvents });
  }
}) => {
  // Ensure timeline has all required properties
  const completeTimeline = {
    events: timeline.events || [],
    eras: timeline.eras || [],
    settings: timeline.settings || DEFAULT_SETTINGS
  };
  
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const ember = isDarkMode ? '#8ec5ff' : '#1e88e5';
  const emberBright = isDarkMode ? '#d2e9ff' : '#64b5f6';
  const emberGlow = isDarkMode ? 'rgba(130, 177, 255, 0.35)' : 'rgba(30, 136, 229, 0.18)';
  const emberBorder = isDarkMode ? 'rgba(144, 202, 249, 0.35)' : 'rgba(21, 101, 192, 0.25)';
  const surface = isDarkMode ? 'rgba(5, 12, 25, 0.9)' : 'rgba(232, 244, 255, 0.92)';
  const surfaceElevated = isDarkMode ? 'rgba(12, 26, 45, 0.92)' : '#f4f9ff';
  const controlPaperSx = {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 2,
    p: 2.5,
    mb: 3,
    borderRadius: '18px',
    border: `1px solid ${emberBorder}`,
    background: isDarkMode
      ? 'linear-gradient(135deg, rgba(30, 136, 229, 0.2), rgba(2,12,28,0.85))'
      : 'linear-gradient(135deg, rgba(227, 242, 253, 0.95), rgba(241, 248, 255, 0.92))',
    boxShadow: isDarkMode
      ? '0 15px 40px rgba(0,0,0,0.55)'
      : '0 20px 50px rgba(30, 136, 229, 0.18)',
    backdropFilter: 'blur(12px)'
  };
  const timelinePaperSx = {
    p: 3,
    mb: 3,
    borderRadius: 2,
    background: isDarkMode
      ? 'linear-gradient(145deg, rgba(4,10,20,0.95), rgba(16,32,58,0.92))'
      : 'linear-gradient(145deg, #f4f9ff, #e3f2fd)',
    border: `1px solid ${isDarkMode ? 'rgba(144,202,249,0.2)' : 'rgba(21,101,192,0.18)'}`,
    boxShadow: isDarkMode
      ? '0 20px 45px rgba(0,0,0,0.6)'
      : '0 30px 60px rgba(21, 101, 192, 0.22)'
  };

  const [viewMode, setViewMode] = useState('timeline'); // 'timeline' or 'list'
  const [zoom, setZoom] = useState(1);
  const [activeEra, setActiveEra] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [importanceFilter, setImportanceFilter] = useState('all');
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [showEraManager, setShowEraManager] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [draggingEvent, setDraggingEvent] = useState(null);
  const [dragPosition, setDragPosition] = useState(null);
  const [panState, setPanState] = useState({
    active: false,
    startX: 0,
    scrollLeft: 0
  });
  const [timelineWidth, setTimelineWidth] = useState(0);
  
  const timelineRef = useRef(null);
  
  // Update timeline width on mount and window resize
  useEffect(() => {
    const updateTimelineWidth = () => {
      if (timelineRef.current) {
        setTimelineWidth(timelineRef.current.offsetWidth);
      }
    };
    
    updateTimelineWidth();
    window.addEventListener('resize', updateTimelineWidth);
    
    return () => {
      window.removeEventListener('resize', updateTimelineWidth);
    };
  }, []);
  
  // Filter events by era if needed
  const filteredEvents = activeEra === 'all' 
    ? completeTimeline.events 
    : completeTimeline.events.filter(event => event.era === activeEra);
  
  // Filter events by search term
  const searchFilteredEvents = searchTerm 
    ? filteredEvents.filter(event => 
        event.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.description?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : filteredEvents;
  
  const importanceFilteredEvents = importanceFilter === 'all'
    ? searchFilteredEvents
    : searchFilteredEvents.filter(event => (event.importance || 'medium') === importanceFilter);
  
  const sortedEvents = [...importanceFilteredEvents].sort((a, b) => {
    const yearA = parseFloat(a.year || a.date || 0);
    const yearB = parseFloat(b.year || b.date || 0);
    return yearA - yearB;
  });
  
  // Calculate timeline range and track offsets
  const timelineStart = completeTimeline.settings.startYear;
  const timelineEnd = completeTimeline.settings.endYear;
  const timelineRange = timelineEnd - timelineStart;
  const TIMELINE_EDGE_OFFSET = 4; // percent padding on each side
  const timelineTrackSpan = 100 - (TIMELINE_EDGE_OFFSET * 2);
  const clampTrackPosition = (position) => Math.max(TIMELINE_EDGE_OFFSET, Math.min(100 - TIMELINE_EDGE_OFFSET, position));
  const mapBasePercentToTrack = (percent) => clampTrackPosition(
    TIMELINE_EDGE_OFFSET + ((percent / 100) * timelineTrackSpan)
  );
  
  // Function to calculate position on timeline
  const calculatePosition = (year) => {
    if (!timelineRange) {
      return clampTrackPosition(50);
    }
    const ratio = (year - timelineStart) / timelineRange;
    const position = TIMELINE_EDGE_OFFSET + (ratio * timelineTrackSpan);
    return clampTrackPosition(position);
  };
  
  // Function to calculate year from position
  const calculateYear = (position) => {
    if (!timelineRange) {
      return timelineStart;
    }
    const clamped = clampTrackPosition(position);
    const ratio = (clamped - TIMELINE_EDGE_OFFSET) / timelineTrackSpan;
    const year = Math.round(timelineStart + (timelineRange * ratio));
    return year;
  };
  
  // Handle zoom change
  const handleZoomChange = (event, newValue) => {
    setZoom(newValue);
  };
  
  // Handle zoom in button click
  const handleZoomIn = () => {
    setZoom(Math.min(zoom + 0.2, 2));
  };
  
  // Handle zoom out button click
  const handleZoomOut = () => {
    setZoom(Math.max(zoom - 0.2, 0.5));
  };
  
  // Handle adding a new event
  const handleAddEventClick = () => {
    setSelectedEvent(null);
    setShowEventDialog(true);
  };
  
  // Handle editing an event
  const handleEditEventClick = (event) => {
    setSelectedEvent(event);
    setShowEventDialog(true);
  };
  
  // Handle saving an event
  const handleSaveEvent = (eventData) => {
    if (selectedEvent) {
      // Edit existing event
      onEditEvent(eventData);
    } else {
      // Add new event
      onAddEvent(eventData);
    }
    setShowEventDialog(false);
  };
  
  // Handle managing eras
  const handleManageEras = () => {
    setShowEraManager(true);
  };
  
  // Handle saving timeline settings
  const handleSaveSettings = (settings) => {
    onTimelineChange({
      ...completeTimeline,
      settings: {
        ...completeTimeline.settings,
        ...settings
      }
    });
  };
  
  // Handle drag start
  const handleDragStart = (e, eventData) => {
    // Create a transparent drag image to eliminate ghosting
    const dragImg = document.createElement('div');
    dragImg.style.opacity = '0';
    dragImg.style.position = 'absolute';
    dragImg.style.top = '-1000px';
    document.body.appendChild(dragImg);
    e.dataTransfer.setDragImage(dragImg, 0, 0);
    setTimeout(() => document.body.removeChild(dragImg), 0);
    
    e.dataTransfer.effectAllowed = 'move';
    setDraggingEvent(eventData);
  };
  
  // Handle drag over
  const handleDragOver = (e) => {
    e.preventDefault();
    if (!timelineRef.current || !draggingEvent) return;
    
    const rect = timelineRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const position = (x / rect.width) * 100;
    setDragPosition(clampTrackPosition(position));
  };
  
  // Handle drag end
  const handleDragEnd = () => {
    if (!draggingEvent || dragPosition === null) {
      setDraggingEvent(null);
      setDragPosition(null);
      return;
    }
    
    // Calculate new year based on drag position
    const newYear = calculateYear(dragPosition);
    
    // Update the event with the new year
    const updatedEvent = { ...draggingEvent, year: newYear };
    onEditEvent(updatedEvent);
    
    setDraggingEvent(null);
    setDragPosition(null);
  };
  
  // Handle drag leave
  const handleDragLeave = () => {
    if (!draggingEvent) return;
    setDragPosition(null);
  };
  
  // Handle drop
  const handleDrop = (e) => {
    e.preventDefault();
    handleDragEnd();
  };

  // Handle timeline panning
  const handlePanMouseDown = (e) => {
    if (e.button !== 0 || !timelineRef.current) return;
    const eventNode = e.target.closest('[data-event-node="true"]');
    if (eventNode) return;
    setPanState({
      active: true,
      startX: e.clientX,
      scrollLeft: timelineRef.current.scrollLeft
    });
  };

  useEffect(() => {
    if (!panState.active) return;

    const handleMouseMove = (e) => {
      if (!timelineRef.current) return;
      e.preventDefault();
      const deltaX = e.clientX - panState.startX;
      timelineRef.current.scrollLeft = panState.scrollLeft - deltaX;
    };

    const handleMouseUp = () => {
      setPanState(prev => ({
        ...prev,
        active: false
      }));
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [panState]);

  // Handle mouse wheel zoom
  const handleWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    setZoom(prev => Math.max(0.5, Math.min(2, prev + delta)));
  };
  
  // Calculate the number of year markers to display based on zoom
  const getYearMarkers = () => {
    // Base markers at zoom level 1
    const baseMarkers = [0, 25, 50, 75, 100];
    
    if (zoom <= 0.7) {
      // Fewer markers at low zoom
      return [0, 50, 100];
    } else if (zoom >= 1.5) {
      // More markers at high zoom
      return [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100];
    } else if (zoom >= 1.2) {
      // Medium-high zoom
      return [0, 12.5, 25, 37.5, 50, 62.5, 75, 87.5, 100];
    } else {
      // Default markers
      return baseMarkers;
    }
  };
  
  const getYearLabelAlignment = (position) => {
    if (position <= 5) {
      return {
        transform: 'translateX(0)',
        textAlign: 'left'
      };
    }
    if (position >= 95) {
      return {
        transform: 'translateX(-100%)',
        textAlign: 'right'
      };
    }
    return {
      transform: 'translateX(-50%)',
      textAlign: 'center'
    };
  };

  const importanceOptions = [
    { value: 'all', label: 'All', color: ember },
    { value: 'high', label: 'Major', color: '#64b5f6' },
    { value: 'medium', label: 'Key', color: '#90caf9' },
    { value: 'low', label: 'Minor', color: '#bbdefb' }
  ];

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Typography variant="overline" sx={{ color: emberBright, letterSpacing: 4, fontWeight: 700 }}>
        TIMELINE
      </Typography>
      <Typography variant="h4" sx={{ fontWeight: 600, color: isDarkMode ? '#e3f2fd' : '#0d2756', mb: 1 }}>
        Story Chronology Atelier
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 720 }}>
        Weave eras, milestones, and pivotal events with drag-friendly controls, adaptive zoom, and matching list analytics.
      </Typography>
      
      {/* Timeline Controls */}
      <Paper sx={controlPaperSx} elevation={0}>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <Tooltip title="Add timeline event" arrow>
            <span>
              <IconButton
                onClick={handleAddEventClick}
                aria-label="Add timeline event"
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: 1,
                  border: `1px solid ${emberBorder}`,
                  color: ember,
                  backgroundColor: 'transparent',
                  '&:hover': {
                    backgroundColor: emberGlow
                  }
                }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="era-select-label">Focus Era</InputLabel>
            <Select
              labelId="era-select-label"
              value={activeEra}
              label="Focus Era"
              onChange={(e) => setActiveEra(e.target.value)}
            >
              <MenuItem value="all">All Eras</MenuItem>
              {completeTimeline.eras.map(era => (
                <MenuItem key={era.id} value={era.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        bgcolor: era.color,
                        border: '1px solid rgba(0,0,0,0.15)',
                        mr: 1
                      }}
                    />
                    {era.name}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<FlagIcon />}
            onClick={handleManageEras}
            sx={{
              borderRadius: 1,
              textTransform: 'none',
              borderColor: emberBorder,
              color: ember,
              '&:hover': {
                borderColor: ember,
                background: emberGlow
              }
            }}
          >
            Manage Eras
          </Button>
          <Button
            variant={showSettings ? 'contained' : 'outlined'}
            startIcon={<SettingsIcon />}
            onClick={() => setShowSettings(!showSettings)}
            sx={{
              borderRadius: 1,
              textTransform: 'none',
              color: showSettings ? '#fff' : ember,
              background: showSettings ? 'linear-gradient(120deg, #42a5f5, #1e88e5)' : 'transparent',
              borderColor: emberBorder,
              '&:hover': showSettings
                ? { background: 'linear-gradient(120deg, #64b5f6, #0d47a1)' }
                : { borderColor: ember, background: emberGlow }
            }}
          >
            Settings
          </Button>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'flex-end' }}>
          <TextField
            size="small"
            placeholder="Search events..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{
              minWidth: 240,
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
                bgcolor: surface,
                '& fieldset': { borderColor: 'transparent' },
                '&:hover fieldset': { borderColor: emberBorder },
                '&.Mui-focused fieldset': { borderColor: ember }
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              )
            }}
          />
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {importanceOptions.map(option => (
              <Chip
                key={option.value}
                label={option.label}
                clickable
                onClick={() => setImportanceFilter(option.value)}
                size="small"
                sx={{
                  borderRadius: '999px',
                  fontWeight: 500,
                  bgcolor: importanceFilter === option.value ? option.color : 'transparent',
                  color: importanceFilter === option.value ? (isDarkMode ? '#2b1600' : '#3b1a00') : option.color,
                  border: `1px solid ${option.color}`,
                  transition: 'all 0.2s',
                  '&:hover': {
                    bgcolor: option.color,
                    color: isDarkMode ? '#2b1600' : '#3b1a00'
                  }
                }}
              />
            ))}
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              borderRadius: '18px',
              px: 1.5,
              py: 0.5,
              border: `1px solid ${emberBorder}`,
              background: surface,
              minWidth: 220
            }}
          >
            <IconButton size="small" onClick={handleZoomOut} disabled={zoom <= 0.5} sx={{ color: ember }}>
              <ZoomOutIcon fontSize="small" />
            </IconButton>
            <Slider
              value={zoom}
              min={0.5}
              max={2}
              step={0.1}
              onChange={handleZoomChange}
              size="small"
              sx={{
                flexGrow: 1,
                '& .MuiSlider-thumb': {
                  bgcolor: '#fff',
                  border: `2px solid ${ember}`
                },
                '& .MuiSlider-track': {
                  bgcolor: ember
                },
                '& .MuiSlider-rail': {
                  bgcolor: 'rgba(255,255,255,0.3)'
                }
              }}
            />
            <IconButton size="small" onClick={handleZoomIn} disabled={zoom >= 2} sx={{ color: ember }}>
              <ZoomInIcon fontSize="small" />
            </IconButton>
            <Typography variant="caption" sx={{ fontWeight: 600, minWidth: 42, textAlign: 'right', color: emberBright }}>
              {Math.round(zoom * 100)}%
            </Typography>
          </Box>
        </Box>
      </Paper>
      
      {/* Timeline Settings Panel */}
      <Collapse in={showSettings}>
        <Paper elevation={0} sx={{ p: 2, mb: 3, border: '1px solid rgba(0,0,0,0.08)', borderRadius: '8px' }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
            Timeline Settings
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Start Year"
                type="number"
                fullWidth
                value={completeTimeline.settings.startYear}
                onChange={(e) => {
                  const startYear = parseInt(e.target.value);
                  handleSaveSettings({ startYear });
                }}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="End Year"
                type="number"
                fullWidth
                value={completeTimeline.settings.endYear}
                onChange={(e) => {
                  const endYear = parseInt(e.target.value);
                  handleSaveSettings({ endYear });
                }}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Year Label"
                fullWidth
                value={completeTimeline.settings.yearLabel}
                onChange={(e) => {
                  handleSaveSettings({ yearLabel: e.target.value });
                }}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel id="display-years-label">Display Years</InputLabel>
                <Select
                  labelId="display-years-label"
                  id="display-years"
                  value={completeTimeline.settings.displayYears ? 'yes' : 'no'}
                  label="Display Years"
                  onChange={(e) => {
                    handleSaveSettings({ displayYears: e.target.value === 'yes' });
                  }}
                  size="small"
                >
                  <MenuItem value="yes">Yes</MenuItem>
                  <MenuItem value="no">No</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>
      </Collapse>
      
      {/* View Mode Tabs */}
      <Box sx={{ mb: 2 }}>
        <Tabs 
          value={viewMode} 
          onChange={(e, newValue) => setViewMode(newValue)}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab 
            value="timeline" 
            label="Timeline View" 
            icon={<ViewTimelineIcon />} 
            iconPosition="start"
          />
          <Tab 
            value="list" 
            label="List View" 
            icon={<ViewListIcon />} 
            iconPosition="start"
          />
        </Tabs>
      </Box>
      
      {/* Timeline View */}
      {viewMode === 'timeline' && (
        <Paper elevation={0} sx={timelinePaperSx}>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', md: 'center' },
              gap: 2
            }}
          >
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: emberBright, letterSpacing: 1, mb: 0.5 }}>
                Cinematic Timeline
              </Typography>
              <Typography variant="body2" color={isDarkMode ? 'rgba(255,255,255,0.7)' : 'text.secondary'}>
                {completeTimeline.settings.startYear} – {completeTimeline.settings.endYear} {completeTimeline.settings.yearLabel} • {sortedEvents.length} curated events
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                icon={<EventIcon sx={{ color: ember }} />}
                label={`${sortedEvents.length} events`}
                sx={{ borderRadius: 1, bgcolor: emberGlow, color: isDarkMode ? '#ffe8c2' : '#8b4600' }}
              />
              <Chip
                icon={<TimelineIcon sx={{ color: ember }} />}
                label={`${completeTimeline.eras.length} eras`}
                sx={{ borderRadius: 1, bgcolor: emberGlow, color: isDarkMode ? '#ffe8c2' : '#8b4600' }}
              />
            </Box>
          </Box>
          
          {sortedEvents.length === 0 ? (
            <Box
              sx={{
                mt: 4,
                p: 4,
                borderRadius: 2,
                border: `1px dashed ${emberBorder}`,
                textAlign: 'center',
                color: isDarkMode ? '#cfe8ff' : '#0b2d4f',
                bgcolor: isDarkMode ? 'rgba(8,20,40,0.65)' : 'rgba(225, 242, 255, 0.65)'
              }}
            >
              <Box
                sx={{
                  textAlign: 'center',
                  py: 6,
                  color: 'text.secondary'
                }}
              >
                <Typography variant="h6" sx={{ mb: 1 }}>
                  No timeline events yet
                </Typography>
                <Typography variant="body2">
                  Tap the “+” button above to start weaving milestones into your chronology.
                </Typography>
              </Box>
            </Box>
          ) : (
            <Box
              ref={timelineRef}
              sx={{
                position: 'relative',
                mt: 4,
                mb: 1,
                height: 240,
                transition: 'height 0.3s ease',
                cursor: draggingEvent ? 'grabbing' : panState.active ? 'grabbing' : 'grab',
                overflowX: 'auto',
                overflowY: 'hidden',
                pr: 2,
                '&::-webkit-scrollbar': {
                  height: 8
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'rgba(0,0,0,0.25)',
                  borderRadius: 4
                }
              }}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onDragLeave={handleDragLeave}
              onMouseDown={handlePanMouseDown}
              onWheel={handleWheel}
            >
              <Box
                sx={{
                  position: 'relative',
                  width: `${100 * zoom}%`,
                  height: '100%',
                  minWidth: '100%',
                  px: { xs: 3, md: 5 },
                  boxSizing: 'border-box',
                  background: isDarkMode
                    ? 'linear-gradient(180deg, rgba(5,15,30,0.7) 0%, rgba(10,24,48,0.95) 60%, rgba(5,15,30,0.75) 100%)'
                    : 'linear-gradient(180deg, rgba(237, 247, 255, 0.9) 0%, rgba(225, 239, 252, 0.98) 60%, rgba(237,247,255,0.92) 100%)',
                  borderRadius: 2,
                  border: `1px solid ${emberBorder}`,
                  boxShadow: isDarkMode ? 'inset 0 0 40px rgba(0,0,0,0.55)' : 'inset 0 0 40px rgba(30, 136, 229, 0.12)',
                  overflow: 'visible'
                }}
              >
                {/* Era ribbons */}
                {completeTimeline.eras.map(era => {
                  if (era.id === 'default') return null;
                  const startPos = calculatePosition(era.startYear || era.startDate);
                  const endPos = calculatePosition(era.endYear || era.endDate);
                  const width = endPos - startPos;
                  
                  return (
                    <Box
                      key={era.id}
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: `${startPos}%`,
                        width: `${width}%`,
                        height: 28,
                        borderRadius: '18px',
                        transform: 'translateY(-50%)',
                        background: `linear-gradient(90deg, ${era.color || '#ffa726'}33, ${era.color || '#ffa726'}11)`,
                        border: `1px solid ${(era.color || ember)}55`,
                        zIndex: 0,
                        filter: 'blur(0.3px)'
                      }}
                    />
                  );
                })}
                
                {/* Timeline spine */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: 0,
                    right: 0,
                    height: 6,
                    borderRadius: 999,
                    transform: 'translateY(-50%)',
                    background: `linear-gradient(90deg, transparent, ${ember}, transparent)`,
                    boxShadow: `0 0 25px ${emberGlow}`,
                    zIndex: 1
                  }}
                />
                
                {/* Year ticks */}
                {getYearMarkers().map(position => (
                  <Box
                    key={position}
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: `${position}%`,
                      height: 20,
                      width: 2,
                      bgcolor: isDarkMode ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.15)',
                      transform: 'translate(-50%, -50%)',
                      zIndex: 2
                    }}
                  />
                ))}
                {getYearMarkers().map(position => {
                  const year = Math.round(timelineStart + (timelineRange * (position / 100)));
                  const labelAlign = getYearLabelAlignment(position);
                  return (
                    <Typography
                      key={`label-${position}`}
                      variant="caption"
                      sx={{
                        position: 'absolute',
                        top: 'calc(50% + 18px)',
                        left: `${position}%`,
                        transform: labelAlign.transform,
                        textAlign: labelAlign.textAlign,
                        fontWeight: 600,
                        letterSpacing: 0.5,
                        color: isDarkMode ? 'rgba(255,255,255,0.8)' : '#5c3310',
                        textShadow: isDarkMode ? '0 2px 4px rgba(0,0,0,0.45)' : 'none'
                      }}
                    >
                      {year}
                    </Typography>
                  );
                })}
                
                {/* Drag indicator */}
                {dragPosition !== null && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '8%',
                      bottom: '8%',
                      left: `${dragPosition}%`,
                      width: 2,
                      background: `linear-gradient(180deg, transparent, ${ember}, transparent)`,
                      boxShadow: `0 0 18px ${emberGlow}`,
                      zIndex: 15
                    }}
                  />
                )}
                
                {/* Event cards */}
                {(() => {
                  const zoomNormalized = Math.max(0.5, Math.min(zoom, 2));
                  const bubbleScale = 0.55 + ((zoomNormalized - 0.5) / 1.5) * 0.45; // scales 0.55 → 1.0
                  return sortedEvents.map((event, index) => {
                    const position = calculatePosition(event.year || event.date);
                    const era = completeTimeline.eras.find(e => e.id === event.era) || completeTimeline.eras[0] || { color: '#1e88e5', name: 'Story Era' };
                    const isDragging = draggingEvent && draggingEvent.id === event.id;
                    const laneIsTop = index % 2 === 0;
                    const connectorHeight = (22 + zoom * 10) * bubbleScale;
                    const statusColor = era.color || ember;
                    const anchorSize = 16 * bubbleScale;
                    const cardMinWidth = 170 * bubbleScale;
                    const cardMaxWidth = 250 * bubbleScale;
                    const cardPaddingX = 1.6 * bubbleScale;
                    const cardPaddingY = 1.1 * bubbleScale;
                    const titleFontScale = Math.max(0.85, bubbleScale);
                    const bodyFontScale = Math.max(0.8, bubbleScale - 0.1);
                    
                    const anchorDot = (
                      <Box
                        sx={{
                          width: anchorSize,
                          height: anchorSize,
                          borderRadius: '50%',
                          background: statusColor,
                          border: `${Math.max(2, 3 * bubbleScale)}px solid rgba(255,255,255,0.85)`,
                          boxShadow: `0 0 14px ${statusColor}44`
                        }}
                      />
                    );
                    
                    const connector = (
                      <Box
                        sx={{
                          width: 2,
                          height: connectorHeight,
                          background: `linear-gradient(180deg, ${statusColor}, transparent)`,
                          transform: laneIsTop ? 'scaleY(1)' : 'rotate(180deg)',
                          filter: 'drop-shadow(0 0 6px rgba(0,0,0,0.35))'
                        }}
                      />
                    );
                    
                    const card = (
                      <Paper
                        elevation={0}
                        sx={{
                          position: 'relative',
                          minWidth: cardMinWidth,
                          maxWidth: cardMaxWidth,
                          borderRadius: 2,
                          px: cardPaddingX,
                          py: cardPaddingY,
                          cursor: 'pointer',
                          background: isDarkMode
                            ? 'linear-gradient(135deg, rgba(255,255,255,0.05), rgba(0,0,0,0.65))'
                            : 'linear-gradient(135deg, rgba(255,255,255,0.95), rgba(255,248,240,0.9))',
                          border: `1px solid ${(statusColor || ember)}55`,
                          boxShadow: isDragging
                            ? '0 18px 35px rgba(0,0,0,0.45)'
                            : '0 14px 30px rgba(0,0,0,0.15)',
                          transform: `scale(${isDragging ? bubbleScale * 1.05 : bubbleScale})`,
                          transformOrigin: laneIsTop ? 'bottom center' : 'top center',
                          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                          color: isDarkMode ? '#fff6ec' : '#3f2200',
                          '&:hover .event-actions': {
                            opacity: 1
                          }
                        }}
                      >
                        {/* Delete button - show on hover */}
                        <Tooltip title="Delete" arrow>
                          <IconButton
                            className="event-actions"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (window.confirm(`Delete "${event.title}"?`)) {
                                onDeleteEvent(event.id);
                              }
                            }}
                            sx={{
                              position: 'absolute',
                              top: -12,
                              right: -12,
                              width: 24,
                              height: 24,
                              opacity: 0,
                              transition: 'opacity 0.2s',
                              bgcolor: 'error.main',
                              color: '#fff',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                              '&:hover': { bgcolor: 'error.dark', transform: 'scale(1.1)' }
                            }}
                          >
                            <DeleteIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                        <Box 
                          onClick={() => !isDragging && handleEditEventClick(event)}
                          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 * bubbleScale }}
                        >
                          <Typography variant="overline" sx={{ letterSpacing: 1, color: emberBright, fontSize: `${0.7 * titleFontScale}rem` }}>
                            {era.name || 'Era'}
                          </Typography>
                          <Chip
                            size="small"
                            label={(event.importance || 'medium').toUpperCase()}
                            sx={{
                              height: 22 * bubbleScale,
                              fontSize: `${0.55 * titleFontScale}rem`,
                              borderRadius: '999px',
                              background: `${statusColor}22`,
                              color: statusColor,
                              border: `1px solid ${statusColor}55`
                            }}
                          />
                        </Box>
                        <Box onClick={() => !isDragging && handleEditEventClick(event)}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: `${1 * titleFontScale}rem` }}>
                            {event.title || event.name}
                          </Typography>
                          <Typography variant="body2" sx={{ opacity: 0.85, mb: 0.5, fontSize: `${0.85 * bodyFontScale}rem` }}>
                            {event.year || event.date} {completeTimeline.settings.yearLabel}
                          </Typography>
                          {event.description && (
                            <Typography variant="body2" sx={{ opacity: 0.75, fontSize: `${0.78 * bodyFontScale}rem` }}>
                              {event.description.length > 110 ? `${event.description.slice(0, 110)}…` : event.description}
                            </Typography>
                          )}
                        </Box>
                      </Paper>
                    );
                    
                    const stack = laneIsTop
                      ? [card, connector, anchorDot]
                      : [anchorDot, connector, card];
                    
                    return (
                      <Box
                        key={event.id}
                        data-event-node="true"
                        draggable
                        onDragStart={(e) => handleDragStart(e, event)}
                        onDragEnd={handleDragEnd}
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: isDragging && dragPosition !== null ? `${dragPosition}%` : `${position}%`,
                          transform: 'translate(-50%, -50%)',
                          zIndex: isDragging ? 25 : 5 + (sortedEvents.length - index),
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 1.2 * bubbleScale,
                          pointerEvents: 'auto',
                          cursor: isDragging ? 'grabbing' : 'grab',
                          opacity: isDragging ? 0.95 : 1,
                          willChange: isDragging ? 'left, transform' : 'auto',
                          transition: isDragging ? 'none' : 'left 0.15s ease-out, opacity 0.2s'
                        }}
                      >
                        {stack}
                      </Box>
                    );
                  });
                })()}
              </Box>
            </Box>
          )}
        </Paper>
      )}
      
      {/* List View */}
      {viewMode === 'list' && (
        <TimelineEventList 
          events={completeTimeline.events}
          eras={completeTimeline.eras}
          settings={completeTimeline.settings}
          onEditEvent={handleEditEventClick}
          onDeleteEvent={onDeleteEvent}
        />
      )}
      
      {/* Event Dialog */}
      <TimelineEventDialog
        open={showEventDialog}
        onClose={() => setShowEventDialog(false)}
        event={selectedEvent}
        timeline={completeTimeline}
        onSave={handleSaveEvent}
      />
      
      {/* Era Manager Dialog */}
      <TimelineEraManager
        open={showEraManager}
        onClose={() => setShowEraManager(false)}
        timeline={completeTimeline}
        onSave={(updatedTimeline) => {
          onTimelineChange(updatedTimeline);
          setShowEraManager(false);
        }}
        onAddEra={(era) => {
          const updatedEras = [...completeTimeline.eras, era];
          onTimelineChange({
            ...completeTimeline,
            eras: updatedEras
          });
        }}
        onEditEra={(era) => {
          const updatedEras = completeTimeline.eras.map(e => 
            e.id === era.id ? era : e
          );
          onTimelineChange({
            ...completeTimeline,
            eras: updatedEras
          });
        }}
        onDeleteEra={(eraId) => {
          const updatedEras = completeTimeline.eras.filter(e => e.id !== eraId);
          
          // Update events that were in this era to have no era
          const updatedEvents = completeTimeline.events.map(event => 
            event.era === eraId ? { ...event, era: '' } : event
          );
          
          onTimelineChange({
            ...completeTimeline,
            eras: updatedEras,
            events: updatedEvents
          });
        }}
      />
    </Box>
  );
};

export default TimelineEditor;
